import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { translations, Language } from "./translations";
import { getEquivalentPath } from "./routeMap";

type LanguageContextType = {
  language: Language;
  t: (key: string) => string;
  getLocalizedPath: (path: string) => string;
  switchLanguagePath: () => string;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return path; // Return the key if not found
    }
  }

  return typeof current === "string" ? current : path;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const language: Language = useMemo(() => {
    // Check if the path starts with /en
    return location.pathname.startsWith("/en") ? "en" : "fr";
  }, [location.pathname]);

  const t = useMemo(() => {
    return (key: string): string => {
      const value = getNestedValue(
        translations[language] as unknown as Record<string, unknown>,
        key
      );
      return value;
    };
  }, [language]);

  const getLocalizedPath = useMemo(() => {
    return (path: string): string => {
      // Remove leading slash for consistency
      const cleanPath = path.startsWith("/") ? path.slice(1) : path;

      if (language === "en") {
        // For English, prepend /en
        return cleanPath ? `/en/${cleanPath}` : "/en";
      } else {
        // For French (default), use path as-is
        return `/${cleanPath}`;
      }
    };
  }, [language]);

  const switchLanguagePath = useMemo(() => {
    return (): string => {
      // Look up the equivalent localized path in the other language. Slugs
      // differ per language for many pages, so a plain "/en" prefix swap would
      // 404. routeMap handles the mapping (with a prefix-swap fallback).
      return getEquivalentPath(location.pathname, language);
    };
  }, [language, location.pathname]);

  const value = useMemo(
    () => ({
      language,
      t,
      getLocalizedPath,
      switchLanguagePath,
    }),
    [language, t, getLocalizedPath, switchLanguagePath]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export function useTranslation() {
  const { t } = useLanguage();
  return { t };
}
